import { TextSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, PaletteColor, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useTheme } from '@mui/system'
export type ChipVariant = 'main' | 'light' | 'dark'

export interface ChipProps {
  textKey: string
  paletteColor?: PaletteColor
  isLoading?: boolean
  variant?: ChipVariant
}

/**
 * A simple Chip component that displays a text label with a background color.
 * Can e.g. be used to display categories or tags.
 *
 * @param param0
 * @story - linked to storybook
 */
const Chip: React.FC<ChipProps> = ({ textKey, paletteColor, isLoading = true, variant = 'light' }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()

  const neutralPalette = useMemo(
    () => ({
      light: theme.palette.grey.grey05,
      main: theme.palette.grey.grey06,
      dark: theme.palette.grey.grey07,
      contrastText: theme.palette.grey.grey00,
    }),
    [theme]
  )

  const variantColorMap = useMemo(
    () => ({
      light: paletteColor?.dark || neutralPalette.dark,
      main: paletteColor?.contrastText || neutralPalette.contrastText,
      dark: paletteColor?.contrastText || neutralPalette.contrastText,
    }),
    [paletteColor, neutralPalette]
  )

  if (isLoading) {
    return (
      <ChipContainer paletteColor={paletteColor ?? neutralPalette} variant={variant}>
        <TextSkeleton variant="subtitle2" sx={{ minWidth: 80 }} />
      </ChipContainer>
    )
  }

  if (!textKey) return null

  return (
    <ChipContainer paletteColor={paletteColor ?? neutralPalette} variant={variant}>
      <Typography noWrap variant="subtitle2" sx={{ pr: 1, pl: 1, color: variantColorMap[variant], textAlign: 'center', fontSize: '13px' }}>
        {t(textKey)}
      </Typography>
    </ChipContainer>
  )
}

interface ChipContainerProps {
  paletteColor: PaletteColor
  children: React.ReactElement
  variant: ChipVariant
}

const ChipContainer: React.FC<ChipContainerProps> = ({ paletteColor, children, variant }) => {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        padding: '4px',
        alignItems: 'center',
        borderRadius: '16px',
        backgroundColor: paletteColor[variant],
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  )
}

export default Chip
